import {
  Divider,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { Grid } from "../common/Grid";
import { GridItem } from "../common/GridItem";
import { FormSection } from "./FormSection";
import { SignSizeForm } from "../form/SignSizeForm";
import React, { useEffect, useRef, useState } from "react";
import { SignColorForm } from "../form/SignColorForm";
import { SignTextForm } from "../form/SignTextForm";
import { SignSymbolForm } from "../form/SignSymbolForm";
import { useForm } from "react-hook-form";
import { ISignForm } from "../../interfaces/ISignForm";
import { ITextFieldErrorText } from "../../interfaces/ITextFieldErrorText";
import { ISignGenerate } from "../../interfaces/ISignGenerate";
import { SignPreview } from "./SignPreview";
import { CheckoutModal } from "../modal/CheckoutModal";
import { SizePopOver, TextPopOver } from "../../helper/textTemplates";
import { useTranslation } from "react-i18next";
import { postOrder } from "../../api/signApi";
import { IOrderStatus } from "../../interfaces/IOrderStatus";
import { OrderConfirmationSnapshot } from "../order/OrderConfirmationSnapshot";
import { calculateSignPrice } from "../../helper/pricing";
type SignGeneratorProps = {
  generateSign: ISignGenerate;
  setGenerateSign: React.Dispatch<React.SetStateAction<ISignGenerate>>;
};

const useStyles = makeStyles({
  container: {
    width: "100%",
  },
  formInputs: {
    display: "flex",
    marginRight: "56px",
    "@media (max-width: 600px)": {
      marginRight: "0",
    },
  },
  flexColumn: {
    flexDirection: "column",
  },
  divider: {
    height: "auto",
    "::before": {
      borderRightWidth: tokens.strokeWidthThick,
    },
    "::after": {
      borderRightWidth: tokens.strokeWidthThick,
    },
  },
  formWrapper: {
    display: "flex",
    height: "100%",
  },
});

export const SignGenerator = ({
  generateSign,
  setGenerateSign,
}: SignGeneratorProps) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const signRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState<boolean>(false);
  const [loading, setIsLoading] = useState<boolean>(false);
  const [fieldsWithErrorMessage, setFieldsWithErrorMessage] = useState<
    ITextFieldErrorText[]
  >([]);
  const [orderStatus, setOrderStatus] = useState<IOrderStatus>({
    orderSent: false,
    error: false,
    message: "",
  });
  const signForm = useForm<ISignForm>({
    defaultValues: {
      ...generateSign.defaultValues,
    },
  });
  const { handleSubmit, watch, setValue } = signForm;
  const form = watch();

  useEffect(() => {
    calculateSignPrice(form, setValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch()]);

  const onSubmit = (form: ISignForm) => {
    postOrder(form, setOrderStatus, setIsLoading, t);
  };
  return (
    <div className={styles.container}>
      <form>
        <Grid>
          <GridItem size="5/12">
            <div className={styles.formWrapper}>
              <div
                className={mergeClasses(styles.formInputs, styles.flexColumn)}
              >
                <FormSection
                  sectionText={t("formSection.size")}
                  content={
                    <SignSizeForm
                      signForm={signForm}
                      fieldsWithErrorMessage={fieldsWithErrorMessage}
                      setFieldsWithErrorMessage={setFieldsWithErrorMessage}
                    />
                  }
                  popoverText={<SizePopOver />}
                />
                <FormSection
                  sectionText={t("formSection.color")}
                  content={<SignColorForm signForm={signForm} />}
                />
                <FormSection
                  sectionText={t("formSection.text")}
                  content={
                    <SignTextForm
                      signForm={signForm}
                      fieldsWithErrorMessage={fieldsWithErrorMessage}
                      setFieldsWithErrorMessage={setFieldsWithErrorMessage}
                    />
                  }
                  popoverText={<TextPopOver />}
                />
                <FormSection
                  sectionText={t("formSection.symbol")}
                  content={<SignSymbolForm signForm={signForm} />}
                />
              </div>
              <Divider appearance="brand" className={styles.divider} vertical />
            </div>
          </GridItem>

          <GridItem size="7/12">
            <SignPreview
              signForm={signForm}
              setGenerateSign={setGenerateSign}
              setCheckoutModalOpen={setCheckoutModalOpen}
            />
          </GridItem>

          <CheckoutModal
            signForm={signForm}
            orderStatus={orderStatus}
            signRef={signRef}
            logoRef={logoRef}
            checkoutModalOpen={checkoutModalOpen}
            loading={loading}
            setCheckoutModalOpen={setCheckoutModalOpen}
            setOrderStatus={setOrderStatus}
            setGenerateSign={setGenerateSign}
            onSubmit={handleSubmit(onSubmit)}
          />
        </Grid>
      </form>
      <OrderConfirmationSnapshot
        logoRef={logoRef}
        signRef={signRef}
        watch={watch}
      />
    </div>
  );
};
